import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { CheckCircle2, Users, Wallet, TrendingUp } from "lucide-react";

function Metric({ icon: Icon, label, value, testid }) {
    return (
        <div className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm" data-testid={testid}>
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-tinta-suave">
                <Icon className="h-3 w-3 text-azul-500" />
                {label}
            </div>
            <div className="mt-1 font-display text-lg font-extrabold text-tinta">{value}</div>
        </div>
    );
}

const BARS = [42, 58, 36, 70, 52, 84, 62, 92, 74, 60, 88, 96];

export default function DashboardMockup() {
    const wrapRef = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });

    const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start end", "end start"] });
    const parallaxY = useTransform(scrollYProgress, [0, 1], [28, -28]);

    const handleMove = (e) => {
        const rect = wrapRef.current?.getBoundingClientRect();
        if (!rect) return;
        mx.set((e.clientX - rect.left) / rect.width - 0.5);
        my.set((e.clientY - rect.top) / rect.height - 0.5);
    };

    const handleLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <motion.div style={{ y: parallaxY }} className="relative">
            <motion.div
                ref={wrapRef}
                onMouseMove={handleMove}
                onMouseLeave={handleLeave}
                initial={{ opacity: 0, y: 40, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                style={{ rotateX, rotateY, transformPerspective: 1200 }}
                className="relative"
                data-testid="hero-dashboard-mockup"
            >
                {/* Janela do dashboard */}
                <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_40px_80px_-24px_rgba(6,37,90,0.35)]">
                    <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                        <span className="h-2.5 w-2.5 rounded-full bg-laranja-400" />
                        <span className="ml-3 rounded-md bg-azul-50 px-2 py-0.5 text-[10px] font-semibold text-azul-700">
                            Smart Benefícios · Painel
                        </span>
                    </div>
                    <div className="p-4 sm:p-5">
                        <div className="grid grid-cols-3 gap-3">
                            <Metric icon={Wallet} label="Plano" value="R$ 99+" testid="mock-metric-plano" />
                            <Metric icon={Users} label="Colaboradores" value="128" testid="mock-metric-colaboradores" />
                            <Metric icon={TrendingUp} label="Pedidos" value="37" testid="mock-metric-pedidos" />
                        </div>

                        <div className="mt-4 rounded-xl border border-slate-100 bg-azul-50/50 p-4">
                            <div className="flex items-center justify-between">
                                <p className="text-xs font-bold text-azul-900">Pedidos por mês</p>
                                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-azul-700 shadow-sm">
                                    2025
                                </span>
                            </div>
                            <div className="mt-3 flex h-24 items-end gap-1.5 sm:h-28">
                                {BARS.map((h, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ height: 0 }}
                                        animate={{ height: `${h}%` }}
                                        transition={{ duration: 0.8, delay: 0.9 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                                        className={`flex-1 rounded-t-md ${i === 11 ? "bg-laranja-500" : "bg-azul-500/85"}`}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="mt-4 space-y-2">
                            {[
                                { nome: "Vale-Transporte · Pedidos", status: "Aprovado", cor: "text-emerald-600 bg-emerald-50" },
                                { nome: "Vale-Refeição · Liberação", status: "Processando", cor: "text-azul-700 bg-azul-50" },
                            ].map((linha, i) => (
                                <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                                    <span className="text-xs font-medium text-tinta-medio">{linha.nome}</span>
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${linha.cor}`}>
                                        {linha.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Chips flutuantes */}
                <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -right-3 -top-5 rounded-2xl bg-laranja-500 px-4 py-3 text-white shadow-laranja sm:-right-6"
                    data-testid="hero-chip-preco"
                >
                    <p className="font-display text-xl font-extrabold leading-none">R$ 99</p>
                    <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider opacity-90">planos a partir de</p>
                </motion.div>

                <motion.div
                    animate={{ y: [0, 9, 0] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    className="absolute -bottom-5 -left-3 flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3.5 py-2.5 shadow-card sm:-left-6"
                    data-testid="hero-chip-pedido"
                >
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    <span className="text-xs font-semibold text-tinta">Pedido aprovado</span>
                </motion.div>
            </motion.div>
        </motion.div>
    );
}
